import React from "react";

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4lang_de_DE from "@amcharts/amcharts4/lang/de_DE";
import am4lang_it_IT from "@amcharts/amcharts4/lang/it_IT";

import { formatPeriod, periodDateFormat } from 'lib/Utils';
import colors from 'data/colors.json';
import translationsJson from 'data/translations.json';
import './Chart.css';

am4core.useTheme(am4themes_animated);

const translations: Translations = translationsJson;

interface ChartDataValues {
    [index: number]: number[] | string[] | ChartDataMinMaxValue[];
}

interface ChartDataMinMaxValue {
    min: number;
    max: number;
}

interface ChartSchemaCluster {
    name: string,
    target?: number | string;
}

interface ChartSchemaXSeries {
    index?: number;
    name: string;
    cluster?: ChartSchemaCluster[];
}

interface ChartSchemaYSeries {
    index: number;
    name: string;
    type?: string;
    stacked?: boolean;
}

interface ChartSchemaYAxis {
    label: string;
    series: ChartSchemaYSeries[];
    min?: number;
}

export interface ChartSchema {
    type: string;
    title: string;
    zoom?: number;
    targetName?: string;
    xAxis: ChartSchemaXSeries;
    yAxis: ChartSchemaYAxis[];
    horizontalLine?: number;
}

export interface ChartData {
    name: string;
    values: ChartDataValues;
    provisionalIndices?: number[];
    prognosisIndices?: number[];
    chartSchema: ChartSchema[];
}

interface Props {
    data: ChartData;
    index: number;
    title: string;
    source: string;
    shown: boolean;
    colors: string[];
}

class Chart extends React.Component<Props, {}> {
    public static defaultProps = {
        colors: colors
    };

    private chart: am4charts.XYChart | undefined;
    private divName: string;

    constructor(props: any) {
        super(props);

        this.divName = `chart_${this.props.data.name}_${this.props.index}`;

        const am4themes_astat = (target: any) => {
            if (target instanceof am4core.ColorSet) {
                target.list = this.props.colors.map(color => am4core.color(color));
            }
        }
        
        am4core.useTheme(am4themes_astat);
    }

    private createChart() {
        const getSeriesType = (type: string): am4charts.XYSeries => {
            switch (type) {
                case "line":
                case "area":
                    return new am4charts.LineSeries();
                case "bar":
                case "dumbbell":
                case "clusteredBar":
                    return new am4charts.ColumnSeries();
            }

            // default
            return new am4charts.LineSeries();
        }

        //console.log("Creating chart...");

        const data = this.props.data;
        const chartSchema = data.chartSchema[this.props.index];
        //console.log("Chart input data:", data);
        //console.log("Chart index: ", this.props.index);

        const provisionalIndices = data.provisionalIndices ? new Set(data.provisionalIndices) : new Set<number>();
        const prognosisIndices = data.prognosisIndices ? new Set(data.prognosisIndices) : new Set<number>();

        const chart = am4core.create(this.divName, am4charts.XYChart);
        const chartLanguage = new am4core.Language();
        chartLanguage.locale = window.lang === "de" ? am4lang_de_DE : am4lang_it_IT;
        chart.numberFormatter.language = chartLanguage;
        chart.dateFormatter.language = chartLanguage;

        chart.responsive.enabled = true;

        const isClusteredBarType = chartSchema.type === "clusteredBar";
        const isComposedType = chartSchema.type === "composed";

        // x axis definition
        const [isSimpleDate, isYearDate] = isClusteredBarType ? [false, false] : periodDateFormat(data.values[chartSchema.xAxis.index!][0]);
        const isDateAxis = isSimpleDate || isYearDate;
        const xAxis = chart.xAxes.push(isDateAxis ? new am4charts.DateAxis() : new am4charts.CategoryAxis());
        if (xAxis instanceof am4charts.DateAxis) {
            xAxis.gridIntervals.setAll([
                { timeUnit: "month", count: 1 },
                { timeUnit: "month", count: 2 },
                { timeUnit: "month", count: 3 },
                { timeUnit: "month", count: 6 },
                { timeUnit: "year", count: 1 },
                { timeUnit: "year", count: 2 },
                { timeUnit: "year", count: 5 },
                { timeUnit: "year", count: 10 }
            ]);
            if (isYearDate) {
                chart.dateFormatter.inputDateFormat = "yyyy";
                xAxis.tooltipDateFormat = "yyyy";
            } else {
                chart.dateFormatter.inputDateFormat = "MM-yyyy";
                xAxis.tooltipDateFormat = "MMM yyyy";
                xAxis.periodChangeDateFormats.setKey("month", "[bold]yyyy[/]");
            }
        } else if (!isClusteredBarType) {
            xAxis.dataFields.category = chartSchema.xAxis.name;
        } else {
            // clusteredBarType
            xAxis.dataFields.category = "cluster";
            xAxis.renderer.cellStartLocation = 0.1;
            xAxis.renderer.cellEndLocation = 0.9;
            xAxis.renderer.grid.template.location = 0;
        }
        xAxis.title.text = translations[chartSchema.xAxis.name][window.lang];

        // y axis and series definition
        for (let axisIndex = 0; axisIndex < chartSchema.yAxis.length; axisIndex++) {
            const yAxis = chartSchema.yAxis[axisIndex];
            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            valueAxis.title.text = translations[yAxis.label][window.lang];
            valueAxis.min = yAxis.min;
            if (axisIndex > 0) {
                valueAxis.renderer.opposite = true;
                valueAxis.syncWithAxis = chart.yAxes.getIndex(0) as am4charts.ValueAxis;
            }

            // series definition
            for (const schemaSeries of yAxis.series) {
                let seriesType: am4charts.XYSeries;
                let type: string;
                if (isComposedType) {
                    seriesType = getSeriesType(schemaSeries.type!);
                    type = schemaSeries.type!;
                } else {
                    seriesType = getSeriesType(chartSchema.type);
                    type = chartSchema.type;
                }

                const series = chart.series.push(seriesType);
                if (isDateAxis) {
                    series.dataFields.dateX = "date";
                } else if (!isClusteredBarType) {
                    series.dataFields.categoryX = chartSchema.xAxis.name;
                } else {
                    series.dataFields.categoryX = "cluster";
                }

                if (type === "dumbbell") {
                    series.dataFields.valueY = "max";
                    series.dataFields.openValueY = "min";
                    series.tooltipText = `{name}\n${translations.max[window.lang]}: [bold]{valueY}[/]\n${translations.min[window.lang]}: [bold]{openValueY}[/]\n{note}`;
                    series.sequencedInterpolation = true;
                    series.fillOpacity = 0;
                    series.strokeOpacity = 1;
                    (series as am4charts.ColumnSeries).columns.template.width = 0.01;
                    series.tooltip!.pointerOrientation = "horizontal";

                    const openBullet = series.bullets.push(new am4charts.CircleBullet());
                    openBullet.locationY = 1;
                    let bulletHover = openBullet.states.create("hover");
                    bulletHover.properties.scale = 1.3;

                    const closeBullet = series.bullets.push(new am4charts.CircleBullet());
                    closeBullet.fill = chart.colors.getIndex(chart.colors.list.length - 2);
                    closeBullet.stroke = closeBullet.fill;
                    bulletHover = closeBullet.states.create("hover");
                    bulletHover.properties.scale = 1.3;
                } else {
                    series.dataFields.valueY = schemaSeries.name;
                    series.tooltipText = "{name}: [bold]{valueY}[/] {note}";
                }

                if (isClusteredBarType) {
                    series.events.on("hidden", _ => this.arrangeColumnsEvent(chart));
                    series.events.on("shown", _ => this.arrangeColumnsEvent(chart));
                }

                series.name = isClusteredBarType ? schemaSeries.name : translations[schemaSeries.name][window.lang];
                series.strokeWidth = 2;
                series.yAxis = valueAxis;

                if (series instanceof am4charts.ColumnSeries) {
                    const propertyFields = series.columns.template.propertyFields;
                    propertyFields.fillOpacity = "fillOpacity";
                    propertyFields.strokeDasharray = "columnDash";
                } else {
                    series.propertyFields.strokeDasharray = "lineDash";
                }

                if(schemaSeries.stacked) {
                    series.stacked = true;
                }

                if (type === "area") {
                    series.fillOpacity = 0.6;
                } else if (type === "line") {
                    // make sure that a line series is drawn over bar chart which will have default index of 0
                    series.zIndex = 1;
                }

                // dim other on hover
                if ((chartSchema.yAxis.length > 1 || yAxis.series.length > 1) && series instanceof am4charts.LineSeries) {
                    const segments = series.segments.template;
                    segments.interactionsEnabled = true;

                    const hoverState = segments.states.create("hover");
                    hoverState.properties.strokeWidth = 4;

                    const dimmed = segments.states.create("dimmed");
                    dimmed.properties.stroke = am4core.color("#dadada");

                    segments.events.on("over", event => this.processSeriesOverEvent(chart, event.target.parent?.parent?.parent as am4charts.LineSeries));
                    segments.events.on("out", event => this.processSeriesOutEvent(chart));
                }

                series.tooltip!.exportable = false;
            }
        }

        // create another series for target bullets if targets are defined
        if(isClusteredBarType && chartSchema.targetName) {
            const series = chart.series.push(new am4charts.LineSeries());
            series.strokeOpacity = 0;
            series.name = translations[chartSchema.targetName][window.lang];
            series.dataFields.valueY = "target";
            series.dataFields.categoryX = "cluster";
            series.yAxis = chart.yAxes.getIndex(0)!;
            series.zIndex = 1;
            series.tooltipText = "{name}: [bold]{valueY}[/]";
            series.fill = am4core.color("red");

            const bullet = series.bullets.push(new am4charts.Bullet());
            bullet.nonScalingStroke = true;
            const rectangle = bullet.createChild(am4core.Rectangle);
            rectangle.strokeOpacity = 0.2;
            rectangle.stroke = am4core.color("#ffffff");
            rectangle.verticalCenter = "middle";
            rectangle.horizontalCenter = "middle";
            rectangle.width = 27;
            rectangle.height = 5;
        }

        // create horizontal line
        if(chartSchema.horizontalLine) {
            const line = (chart.yAxes.getIndex(0) as am4charts.ValueAxis).axisRanges.create();
            line.value = chartSchema.horizontalLine;
            line.grid.stroke = am4core.color("#000");
            line.grid.strokeWidth = 2;
            line.grid.strokeOpacity = 1;
            line.grid.above = true;
        }

        // cursor
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.xAxis = xAxis;
        chart.cursor.exportable = false;

        // legend
        chart.legend = new am4charts.Legend();
        chart.legend.position = "bottom";
        chart.legend.fontWeight = "bold";

        // export menu
        chart.events.on("appeared", _ => {
            chart.exporting.menu = new am4core.ExportMenu();
            chart.exporting.menu.items = [
                {
                    "label": translations.chartExport[window.lang],
                    "menu": [
                        { "type": "png", "label": "PNG" },
                        { "type": "jpg", "label": "JPG" },
                        { "type": "svg", "label": "SVG" },
                        { "type": "pdf", "label": "PDF" }
                    ]
                }
            ];
            chart.exporting.menu.verticalAlign = "bottom";
            chart.exporting.menu.items[0].icon = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiA/PjwhRE9DVFlQRSBzdmcgIFBVQkxJQyAnLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4nICAnaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkJz48c3ZnIGhlaWdodD0iMzJweCIgaWQ9IkxheWVyXzEiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDMyIDMyOyIgdmVyc2lvbj0iMS4xIiB2aWV3Qm94PSIwIDAgMzIgMzIiIHdpZHRoPSIzMnB4IiB4bWw6c3BhY2U9InByZXNlcnZlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj48cGF0aCBkPSJNNCwxMGgyNGMxLjEwNCwwLDItMC44OTYsMi0ycy0wLjg5Ni0yLTItMkg0QzIuODk2LDYsMiw2Ljg5NiwyLDhTMi44OTYsMTAsNCwxMHogTTI4LDE0SDRjLTEuMTA0LDAtMiwwLjg5Ni0yLDIgIHMwLjg5NiwyLDIsMmgyNGMxLjEwNCwwLDItMC44OTYsMi0yUzI5LjEwNCwxNCwyOCwxNHogTTI4LDIySDRjLTEuMTA0LDAtMiwwLjg5Ni0yLDJzMC44OTYsMiwyLDJoMjRjMS4xMDQsMCwyLTAuODk2LDItMiAgUzI5LjEwNCwyMiwyOCwyMnoiLz48L3N2Zz4=";
        });

        let options = chart.exporting.getFormatOptions("pdf");
        options.addURL = false;
        options.minWidth = 1000;
        options.minHeight = 1000;
        options.maxWidth = 2000;
        options.maxHeight = 2000;
        chart.exporting.setFormatOptions("pdf", options);
        options = chart.exporting.getFormatOptions("png");
        options.minWidth = 1000;
        options.minHeight = 1000;
        options.maxWidth = 2000;
        options.maxHeight = 2000;
        chart.exporting.setFormatOptions("png", options);
        options = chart.exporting.getFormatOptions("jpg");
        options.minWidth = 1000;
        options.minHeight = 1000;
        options.maxWidth = 2000;
        options.maxHeight = 2000;
        chart.exporting.setFormatOptions("jpg", options);
        options = chart.exporting.getFormatOptions("svg");
        options.minWidth = 1000;
        options.minHeight = 1000;
        options.maxWidth = 2000;
        options.maxHeight = 2000;
        chart.exporting.setFormatOptions("svg", options);
        chart.exporting.filePrefix = `ASTAT_${translations[this.props.title][window.lang]}_${translations[data.name][window.lang]}`;

        // Title and source for export
        const topContainer = chart.chartContainer.createChild(am4core.Container);
        topContainer.layout = "absolute";
        topContainer.toBack();
        topContainer.paddingBottom = 15;
        topContainer.width = am4core.percent(100);
        const title = topContainer.createChild(am4core.Label);
        title.text = (chartSchema.title !== undefined) ? translations[chartSchema.title][window.lang] : translations[data.name][window.lang];
        title.fontWeight = "bold";
        title.align = "center";
        title.disabled = false;
        const source = chart.createChild(am4core.Label);
        source.text = this.props.source;
        source.align = "right";
        source.fillOpacity = 0.8;
        source.fontSize = "10";
        source.disabled = true;
        // Enable title on export
        chart.exporting.events.on("exportstarted", _ => {
            source.disabled = false;
            chart.background.visible = true;
            chart.invalidate();
        });
        // Disable title when export finishes
        chart.exporting.events.on("exportfinished", _ => {
            //title.disabled = true;
            source.disabled = true;
            chart.background.visible = false;
        });
        chart.exporting.events.on("error", _ => {
            source.disabled = true;
            chart.background.visible = false;
        });
        chart.exporting.events.on("exporttimedout", _ => {
            source.disabled = true;
            chart.background.visible = false;
        });
        // Add title to validated sprites
        chart.exporting.validateSprites.push(source);
        chart.exporting.validateSprites.push(chart);

        chart.zoomOutButton.background.fill = am4core.color(this.props.colors[this.props.colors.length - 1]);
        chart.zoomOutButton.background.states.getKey("hover")!.properties.fill = am4core.color(this.props.colors[this.props.colors.length - 2]);
        chart.zoomOutButton.exportable = false;
        chart.zoomOutButton.userClassName = "chart-zoom-button";

        if (!isClusteredBarType) {
            // Create a horizontal scrollbar with preview and place it underneath the date axis
            const scrollbarX = new am4charts.XYChartScrollbar();
            chart.scrollbarX = scrollbarX;
            scrollbarX.series.push(chart.series.getIndex(0)!);
            scrollbarX.minHeight = 35;
            scrollbarX.scrollbarChart.xAxes.getIndex(0)!.renderer.labels.template.disabled = true;
            scrollbarX.parent = chart.bottomAxesContainer;
            //scrollbarX.exportable = false;
            scrollbarX.language = chartLanguage;
            scrollbarX.width = am4core.percent(97);

            if (chartSchema.zoom) {
                const endIndex = data.values[chartSchema.xAxis.index!].length;
                xAxis.zoom({ start: chartSchema.zoom / endIndex, end: 1.0 });
            }
        }

        let decimalCount = 0;
        const countDecimals = (value: number | string | ChartDataMinMaxValue): number => {
            if(!value)
                return 0;

            if (typeof value === 'number' && Math.floor(value) === value) 
                return 0;
            else if(typeof value !== 'number'&& typeof value !== 'string') {
                return Math.max(countDecimals(value.min), countDecimals(value.max));
            }
        
            const str = value.toString();
            return str.split(".")[1].length || 0;
        }

        // data
        const generateNormalData = () => data.values[chartSchema.xAxis.index!].map((xValue, xIndex) => {
            const obj: { [key: string]: number | string | ChartDataMinMaxValue } = {};
            if (isDateAxis) {
                obj.date = xValue;
            } else {
                obj[chartSchema.xAxis.name] = formatPeriod(xValue, window.lang, translations);
            }

            for (const yAxis of chartSchema.yAxis) {
                for (const series of yAxis.series) {
                    const val = data.values[series.index][xIndex];
                    decimalCount = Math.max(decimalCount, countDecimals(val));
                    if (series.type === "dumbbell") {
                        obj.min = (val as ChartDataMinMaxValue).min;
                        obj.max = (val as ChartDataMinMaxValue).max;
                    } else {
                        obj[series.name] = val;
                    }
                }
            }

            if (provisionalIndices.has(xIndex + 1)) {
                obj.lineDash = "5,5";
            } else if(prognosisIndices.has(xIndex + 1)) {
                obj.lineDash = "8,4,2,4";
            }

            if (provisionalIndices.has(xIndex)) {
                obj.columnDash = "5,5";
                obj.note = `(${translations["provisional"][window.lang]})`;
                obj.fillOpacity = 0.3;
            } else if (prognosisIndices.has(xIndex)) {
                obj.columnDash = "8,4,2,4";
                obj.note = `(${translations["prognosis"][window.lang]})`;
                obj.fillOpacity = 0.3;
            }

            return obj;
        });

        const generateClusteredData = () => chartSchema.xAxis.cluster!.map((cluster, clusterIndex) => {
            const obj: { [key: string]: number | string | ChartDataMinMaxValue } = {};
            obj.cluster = translations[cluster.name][window.lang];
            if(cluster.target) {
                obj.target = cluster.target;
            }

            for (const yAxis of chartSchema.yAxis) {
                for (const series of yAxis.series) {
                    const val = data.values[series.index][clusterIndex];
                    obj[series.name] = val;
                    decimalCount = Math.max(decimalCount, countDecimals(val));
                }
            }

            if (provisionalIndices.has(clusterIndex + 1)) {
                obj.lineDash = "5,5";
            } else if(prognosisIndices.has(clusterIndex + 1)) {
                obj.lineDash = "8,4,2,4";
            }

            if (provisionalIndices.has(clusterIndex)) {
                obj.columnDash = "5,5";
                obj.note = `(${translations["provisional"][window.lang]})`;
                obj.fillOpacity = 0.3;
            } else if (prognosisIndices.has(clusterIndex)) {
                obj.columnDash = "8,4,2,4";
                obj.note = `(${translations["prognosis"][window.lang]})`;
                obj.fillOpacity = 0.3;
            }

            return obj;
        });

        const chartData = isClusteredBarType ? generateClusteredData() : generateNormalData();
        switch(decimalCount) {
            case 1:
                chart.numberFormatter.numberFormat = "#,###.0";
                break;
            case 2:
                chart.numberFormatter.numberFormat = "#,###.00";
                break;
        }

        //console.log("Chart data:", chartData);

        chart.data = chartData;
        this.chart = chart;
    }

    private processSeriesOverEvent(chart: am4charts.XYChart, hoveredSeries: am4charts.LineSeries) {
        hoveredSeries.toFront();
        hoveredSeries.legendDataItem.marker.setState("default");
        hoveredSeries.legendDataItem.label.setState("default");
        hoveredSeries.segments.each(segment => segment.setState("hover"));

        chart.series.each(series => {
            if (series === hoveredSeries) {
                return;
            } else if (series instanceof am4charts.LineSeries) {
                series.segments.each(segment => segment.setState("dimmed"));
                series.bulletsContainer.setState("dimmed");
                series.legendDataItem.marker.setState("dimmed");
                series.legendDataItem.label.setState("dimmed");
            }
        });
    }

    private processSeriesOutEvent(chart: am4charts.XYChart) {
        chart.series.each(series => {
            if (series instanceof am4charts.LineSeries) {
                series.segments.each(segment => segment.setState("default"));
                series.bulletsContainer.setState("default");
                series.legendDataItem.marker.setState("default");
                series.legendDataItem.label.setState("default");
            }
        });
    }

    private arrangeColumnsEvent(chart: am4charts.XYChart) {
        const series = chart.series.getIndex(0)!;
        const xAxis = chart.xAxes.getIndex(0)!;

        const w = 1 - xAxis.renderer.cellStartLocation - (1 - xAxis.renderer.cellEndLocation);
        if (series.dataItems.length > 1) {
            const x0 = xAxis.getX(series.dataItems.getIndex(0)!, "categoryX");
            const x1 = xAxis.getX(series.dataItems.getIndex(1)!, "categoryX");
            const delta = ((x1 - x0) / chart.series.length) * w;
            if (am4core.isNumber(delta)) {
                const middle = chart.series.length / 2;

                let newIndex = 0;
                chart.series.each(series => {
                    if (!series.isHidden && !series.isHiding) {
                        series.dummyData = newIndex;
                        newIndex++;
                    } else {
                        series.dummyData = chart.series.indexOf(series);
                    }
                })
                const visibleCount = newIndex;
                const newMiddle = visibleCount / 2;

                chart.series.each(series => {
                    const trueIndex = chart.series.indexOf(series);
                    const newIndex = series.dummyData;
                    const dx = (newIndex - trueIndex + middle - newMiddle) * delta

                    series.animate({ property: "dx", to: dx }, series.interpolationDuration, series.interpolationEasing);
                    //series.bulletsContainer.animate({ property: "dx", to: dx }, series.interpolationDuration, series.interpolationEasing);
                });
            }
        }
    }

    private updateChart() {
        if(this.chart) {
            this.chart.dispose();
            this.createChart();
        }
    }

    componentDidMount() {
        if(this.props.shown) {
            this.createChart();
        }   
    }

    componentDidUpdate(prevProps: Props) {
        if(this.props.shown && !this.chart) {
            this.createChart();
        } else {
            if(prevProps.data.name !== this.props.data.name) {
                this.updateChart();
            } else if(prevProps.index !== this.props.index || prevProps.source !== this.props.source || prevProps.title !== this.props.title) {
                this.updateChart();
            }
        }
    }

    componentWillUnmount() {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    render() {
        return (<div id={this.divName} className="chart" />);
    }
}

export default Chart;
